import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Card, CardContent, Stack, Chip, Checkbox, Button, Alert,
  IconButton, Tooltip, Fade, Zoom, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  ArrowBack, CalendarMonth as CalendarIcon, Save as SaveIcon, Refresh as RefreshIcon,
  CheckCircle as CheckIcon, Lock as LockIcon, LockOpen as LockOpenIcon, Person as PersonIcon,
  Event as EventIcon, DateRange as DateRangeIcon, Star as StarIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import axiosInstance from '../../services/axiosInstance';

const translations = {
  en: {
    title: 'User Fiscal Years Access', subtitle: 'Control which fiscal years this user can access',
    back: 'Back', save: 'Save Changes', refresh: 'Refresh',
    fiscalYear: 'Fiscal Year', period: 'Period', status: 'Status', access: 'Access',
    current: 'Current', closed: 'Closed', open: 'Open', loading: 'Loading...', saved: 'Access saved successfully!',
    noFiscalYears: 'No fiscal years found', user: 'User', grantAccess: 'Grant Access',
    note: 'Note: Current fiscal year is always accessible to all users',
    totalYears: 'Total Years', accessGranted: 'Access Granted', closedYears: 'Closed', openYears: 'Open',
    autoAccess: 'Auto Access'
  },
  ar: {
    title: 'وصول المستخدم للسنوات المالية', subtitle: 'التحكم في السنوات المالية التي يمكن للمستخدم الوصول إليها',
    back: 'رجوع', save: 'حفظ التغييرات', refresh: 'تحديث',
    fiscalYear: 'السنة المالية', period: 'الفترة', status: 'الحالة', access: 'الوصول',
    current: 'الحالية', closed: 'مقفلة', open: 'مفتوحة', loading: 'جاري التحميل...', saved: 'تم حفظ الوصول بنجاح!',
    noFiscalYears: 'لا توجد سنوات مالية', user: 'المستخدم', grantAccess: 'منح الوصول',
    note: 'ملاحظة: السنة المالية الحالية متاحة لجميع المستخدمين تلقائياً',
    totalYears: 'إجمالي السنوات', accessGranted: 'الوصول الممنوح', closedYears: 'مقفلة', openYears: 'مفتوحة',
    autoAccess: 'وصول تلقائي'
  },
};

interface FiscalPeriod {
  fiscalPeriodId: number; periodName: string; startDate: string; endDate: string;
  isCurrent: boolean; isClosed: boolean;
}
interface User { userId: number; userName: string; fullName: string; }

const UserFiscalYearsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';

  const [user, setUser] = useState<User | null>(null);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [userAccess, setUserAccess] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, [userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, periodsRes, accessRes] = await Promise.all([
        axiosInstance.get(`/Users/${userId}`),
        axiosInstance.get('/FiscalPeriods'),
        axiosInstance.get(`/Users/${userId}/fiscal-years`)
      ]);
      setUser(userRes.data);
      setFiscalPeriods(periodsRes.data);
      setUserAccess(new Set(accessRes.data.map((a: any) => a.fiscalPeriodId)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleToggleAccess = (periodId: number) => {
    const newSet = new Set(userAccess);
    if (newSet.has(periodId)) newSet.delete(periodId);
    else newSet.add(periodId);
    setUserAccess(newSet);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/Users/${userId}/fiscal-years`, Array.from(userAccess));
      toast.success(t.saved);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || (isRTL ? 'فشل الحفظ' : 'Failed to save'));
    }
    finally { setSaving(false); }
  };

  const stats = {
    total: fiscalPeriods.length,
    granted: userAccess.size + fiscalPeriods.filter(fp => fp.isCurrent).length,
    closed: fiscalPeriods.filter(fp => fp.isClosed).length,
    open: fiscalPeriods.filter(fp => !fp.isClosed).length
  };

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
      <CircularProgress size={60} sx={{ color: '#667eea' }} />
      <Typography color="text.secondary">{t.loading}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                <ArrowBack />
              </IconButton>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: 56, height: 56 }}>
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.user}: {user?.fullName} ({user?.userName})</Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.refresh} arrow>
                <IconButton onClick={loadData} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, borderRadius: 2, px: 3 }}>
                {t.save}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {[
          { value: stats.total, label: t.totalYears, icon: <CalendarIcon />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          { value: stats.granted, label: t.accessGranted, icon: <CheckIcon />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
          { value: stats.open, label: t.openYears, icon: <LockOpenIcon />, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
          { value: stats.closed, label: t.closedYears, icon: <LockIcon />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        ].map((stat, index) => (
          <Zoom in timeout={300 + index * 100} key={stat.label}>
            <Card elevation={0} sx={{ flex: '1 1 180px', minWidth: 160, background: stat.gradient, color: 'white', borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 15px 30px rgba(0,0,0,0.2)' } }}>
              <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                <Box sx={{ opacity: 0.7, mb: 1 }}>{stat.icon}</Box>
                <Typography variant="h4" fontWeight="bold">{stat.value}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Zoom>
        ))}
      </Box>

      {/* Note */}
      <Fade in timeout={600}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, background: isDark ? 'rgba(79, 172, 254, 0.1)' : 'rgba(79, 172, 254, 0.15)', border: '1px solid rgba(79, 172, 254, 0.3)' }} icon={<EventIcon sx={{ color: '#4facfe' }} />}>
          <Typography sx={{ color: isDark ? '#90caf9' : '#1976d2' }}>{t.note}</Typography>
        </Alert>
      </Fade>

      {/* Fiscal Years Table */}
      <Fade in timeout={700}>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                {[t.fiscalYear, t.period, t.status, t.access].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fiscalPeriods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <CalendarIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary" variant="h6">{t.noFiscalYears}</Typography>
                  </TableCell>
                </TableRow>
              ) : fiscalPeriods.map((fp) => (
                <TableRow key={fp.fiscalPeriodId} hover sx={{
                  bgcolor: fp.isCurrent ? (isDark ? 'rgba(79, 172, 254, 0.1)' : 'rgba(79, 172, 254, 0.05)') : 'inherit',
                  '&:hover': { bgcolor: isDark ? 'rgba(79, 172, 254, 0.15)' : 'rgba(79, 172, 254, 0.08)' },
                  transition: 'background 0.2s ease'
                }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        p: 1.5, borderRadius: 2,
                        background: fp.isCurrent ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : fp.isClosed ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <CalendarIcon />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight="bold" color="primary.main">{fp.periodName}</Typography>
                          {fp.isCurrent && <Chip icon={<StarIcon sx={{ fontSize: 14 }} />} label={t.current} size="small" sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', fontWeight: 600, '& .MuiChip-icon': { color: 'white' } }} />}
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DateRangeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2">
                        {new Date(fp.startDate).toLocaleDateString('en-GB')} - {new Date(fp.endDate).toLocaleDateString('en-GB')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={fp.isClosed ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                      label={fp.isClosed ? t.closed : t.open}
                      size="small"
                      sx={{
                        background: fp.isClosed ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        color: 'white', fontWeight: 600,
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {fp.isCurrent ? (
                      <Chip icon={<CheckIcon sx={{ fontSize: 16 }} />} label={t.autoAccess} size="small"
                        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, '& .MuiChip-icon': { color: 'white' } }} />
                    ) : (
                      <Checkbox
                        checked={userAccess.has(fp.fiscalPeriodId)}
                        onChange={() => handleToggleAccess(fp.fiscalPeriodId)}
                        sx={{
                          color: '#4facfe',
                          '&.Mui-checked': { color: '#4facfe' },
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          p: 0.5,
                          transition: 'transform 0.2s ease',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Fade>
    </Box>
  );
};

export default UserFiscalYearsPage;